import { isNil } from 'lodash';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { CardFormContext } from '@shared/components/blocks/contexts/form';
import { MBBlockLabel } from '@shared/components/blocks/utils/label';
import { MBIconV2, MBIconV2Name } from '@shared/components/image';
import { MBTextInput } from '@shared/components/textInput';
import { MBTextBody1 } from '@shared/components/typography';
import { useSelector } from '@shared/store';
import { ColorThemeContext } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { notNil } from '@shared/util/notNil';
import { validateEmails } from '@shared/util/validate/email';
import { isInputComponent } from '../../utils';
import { WorkflowComponentProps } from './WorkflowComponent';
import { WorkflowComponentWrapper } from './WorkflowComponentWrapper';
import { WorkflowTextComponent } from './WorkflowTextComponent';

export function LookupEmailComponent({
  initialData,
  ...props
}: Omit<WorkflowComponentProps, 'initialData'> & {
  initialData: string[][] | undefined;
}) {
  return (
    <WorkflowTextComponent
      {...props}
      initialData={isNil(initialData) ? undefined : initialData.flat()}
      component={props.component}
      showIcon={props.showIcon}
    />
  );
}

export function WorkflowEmailComponentLowCode(props: WorkflowComponentProps) {
  const { setFieldError, errors } = useContext(CardFormContext);
  return <WorkflowEmailComponent {...props} setFieldError={setFieldError} errors={errors} />;
}

export function WorkflowEmailComponent({
  initialData,
  component,
  onDataChanged,
  isReadOnly,
  showLabel,
  paddingHorizontal,
  iconPad,
  setFieldError,
  errors,
}: WorkflowComponentProps & {
  setFieldError: (id: string, error: string | undefined) => void;
  errors: { [key: string]: string };
}) {
  const colors = useContext(ColorThemeContext);
  const emailComponent = component as MBAPI.WorkflowTemplate_components_EmailComponent;
  const error = errors[component.id];
  const [inputValue, setInputValue] = useState<string>(
    initialData?.length ? initialData.join(', ') : '',
  );
  const [isValid, setIsValid] = useState<boolean>(true);
  const [checkedInitialValidity, setCheckedInitialValidity] = useState(false);
  const readOnly = isReadOnly || emailComponent.readonly;
  const viewportWidth = useSelector(state => state.appState.viewportWidth);
  const inputWidth = viewportWidth > 400 ? 335 : 275;

  useEffect(() => {
    if (checkedInitialValidity || !initialData) {
      return;
    }
    setIsValid(validateEmails(initialData));
    setCheckedInitialValidity(true);
  }, [checkedInitialValidity, initialData]);

  const onChange = useCallback(
    (input: string) => {
      const newValue = input ? input.replace(/ /g, '').split(',').filter(notNil) : null;
      setInputValue(input);
      setIsValid(validateEmails(newValue));
      onDataChanged?.(emailComponent.id, newValue);
    },
    [onDataChanged, emailComponent],
  );

  const onFocus = () => {
    setIsValid(true);
    setFieldError(emailComponent.id, undefined);
  };

  const setValidityMessage = useCallback(() => {
    if (!isValid) {
      setFieldError(emailComponent.id, i18n('workflowTemplate.component.error.email.invalid'));
    }
  }, [isValid, setFieldError, emailComponent]);

  const onBlur = setValidityMessage;

  if (!isInputComponent(emailComponent)) {
    return null;
  }

  if (readOnly && !initialData?.length) {
    return null;
  }

  if (readOnly && initialData?.length) {
    return (
      <View centerVertically row paddingHorizontal={paddingHorizontal} paddingVertical={1}>
        {!isNil(emailComponent.icon) && (
          <MBView paddingRight={iconPad} style={[{ flexShrink: 0, flexGrow: 0 }]}>
            <MBIconV2
              color={(() => {
                if (!isNil(error)) {
                  return colors.error.foreground;
                }
                return colors.accent;
              })()}
              name={emailComponent.icon as MBIconV2Name}
              size={30}
            />
          </MBView>
        )}
        <View>
          {showLabel && (
            <View>
              <MBBlockLabel label={emailComponent.label} />
            </View>
          )}
          {initialData.map((email: string) => (
            <MBTextBody1 key={email}>{email}</MBTextBody1>
          ))}
        </View>
      </View>
    );
  }

  return (
    <WorkflowComponentWrapper
      paddingHorizontal={paddingHorizontal ?? 0}
      iconPad={iconPad ?? 0}
      component={emailComponent}
      showLabel={!!showLabel}
      error={error}
    >
      <View fullWidth>
        <MBTextInput
          noBorder
          noPadding
          inputMode="email"
          textContentType="emailAddress"
          value={inputValue}
          onChangeText={onChange}
          placeholder={emailComponent.placeholder ?? undefined}
          onBlur={onBlur}
          onFocus={onFocus}
          style={{ width: inputWidth }}
        />
      </View>
    </WorkflowComponentWrapper>
  );
}
